import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Save, AlertCircle, CheckCircle, ExternalLink, Copy, RefreshCw, Shield, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';

export default function AWSSettingsPage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const [config, setConfig] = useState({
    access_key_id: '',
    secret_access_key: '',
    region: 'us-east-1'
  });
  const [hasConfig, setHasConfig] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [awsAccountId, setAwsAccountId] = useState(null);
  const [setupInstructions, setSetupInstructions] = useState(null);
  const [testError, setTestError] = useState(null);

  useEffect(() => {
    fetchConfig();
    fetchSetupInstructions();
  }, []);

  const fetchConfig = async () => {
    try {
      const response = await axios.get(`${API}/aws/config`);
      if (response.data.configured) {
        setConfig(prev => ({
          ...prev,
          region: response.data.region || 'us-east-1'
        }));
        setHasConfig(response.data.has_credentials);
        setIsConnected(response.data.is_connected || false);
        setAwsAccountId(response.data.aws_account_id || null);
      }
    } catch (error) {
      console.error('Failed to fetch AWS config:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSetupInstructions = async () => {
    try {
      const response = await axios.get(`${API}/aws/setup-instructions`);
      setSetupInstructions(response.data);
    } catch (error) {
      console.error('Failed to fetch setup instructions:', error);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setTestError(null);
    
    if (!config.access_key_id || !config.secret_access_key) {
      toast.error('Please enter both Access Key ID and Secret Access Key');
      return;
    }

    if (!config.access_key_id.startsWith('AKIA')) {
      toast.error('Invalid Access Key ID format. It should start with AKIA');
      return;
    }

    setSaving(true);
    try {
      await axios.post(`${API}/aws/config`, config);
      toast.success('AWS credentials saved successfully!');
      setHasConfig(true);
      // Auto-test connection after saving
      handleTestConnection();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async () => {
    setTesting(true);
    setTestError(null);
    try {
      const response = await axios.post(`${API}/aws/test-connection`);
      toast.success(response.data.message || 'Successfully connected to AWS!');
      setIsConnected(true);
      setAwsAccountId(response.data.account_id);
      fetchConfig();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Connection test failed';
      setTestError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setTesting(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Are you sure you want to disconnect AWS? This will remove your credentials.')) {
      return;
    }
    try {
      await axios.delete(`${API}/aws/disconnect`);
      toast.success('AWS disconnected');
      setIsConnected(false);
      setHasConfig(false);
      setAwsAccountId(null);
      setConfig({ access_key_id: '', secret_access_key: '', region: 'us-east-1' });
    } catch (error) {
      toast.error('Failed to disconnect');
    }
  };

  const copyToClipboard = (text, label = 'Value') => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied!`);
  };

  const awsRegions = [
    { value: 'us-east-1', label: 'US East (N. Virginia)' },
    { value: 'us-east-2', label: 'US East (Ohio)' },
    { value: 'us-west-1', label: 'US West (N. California)' },
    { value: 'us-west-2', label: 'US West (Oregon)' },
    { value: 'eu-west-1', label: 'Europe (Ireland)' },
    { value: 'eu-west-2', label: 'Europe (London)' },
    { value: 'eu-central-1', label: 'Europe (Frankfurt)' },
    { value: 'ap-south-1', label: 'Asia Pacific (Mumbai)' },
    { value: 'ap-southeast-1', label: 'Asia Pacific (Singapore)' },
    { value: 'ap-southeast-2', label: 'Asia Pacific (Sydney)' },
    { value: 'ap-northeast-1', label: 'Asia Pacific (Tokyo)' }
  ];

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-slate-200 rounded w-1/3"></div>
          <div className="h-32 bg-slate-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-heading font-semibold tracking-tight text-slate-900">
          AWS Integration Setup
        </h1>
        <p className="text-slate-600 mt-2">
          Connect your AWS account to sync Security Hub findings with your compliance controls
        </p>
      </div>

      {/* Connection Status */}
      {isConnected && (
        <div className="bg-green-50 border border-green-200 rounded-sm p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <div>
                <p className="font-medium text-green-900">Connected to AWS</p>
                <p className="text-sm text-green-700">
                  Account ID: {awsAccountId}
                </p>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleDisconnect}
              className="text-red-600 hover:bg-red-50 rounded-sm"
            >
              Disconnect
            </Button>
          </div>
        </div>
      )}

      {testError && (
        <div className="bg-red-50 border border-red-200 rounded-sm p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
            <div>
              <p className="font-medium text-red-900">Connection Failed</p>
              <p className="text-sm text-red-700">{testError}</p>
            </div>
          </div>
        </div>
      )}

      {/* Step-by-Step Instructions */}
      <div className="bg-white border border-slate-200 rounded-sm">
        <div className="p-4 border-b border-slate-200 bg-slate-50">
          <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
            <Shield className="w-5 h-5" />
            Setup Instructions
          </h2>
          <p className="text-sm text-slate-600 mt-1">
            Create an IAM User in your AWS account with Security Hub read access
          </p>
        </div>

        <div className="p-4 space-y-4">
          {setupInstructions?.instructions?.map((instruction, index) => (
            <div key={index} className="flex gap-4">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-slate-900 text-white flex items-center justify-center text-sm font-medium">
                {instruction.step}
              </div>
              <div className="flex-1 pt-1">
                <p className="font-medium text-slate-900">{instruction.title}</p>
                <p className="text-sm text-slate-600 mt-0.5">{instruction.description}</p>
              </div>
            </div>
          ))}

          {/* Required Policy Info */}
          {setupInstructions?.required_policy && (
            <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-sm">
              <p className="text-sm text-blue-900">
                <strong>Required Policy:</strong> {setupInstructions.required_policy}
              </p>
              <p className="text-xs text-blue-700 mt-1">
                This is an AWS managed policy that grants read-only access to Security Hub
              </p>
            </div>
          )}

          {/* AWS Console Link */}
          <div className="pt-4 border-t border-slate-200">
            <a
              href="https://console.aws.amazon.com/iam/home#/users$new?step=details"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-800 font-medium"
            >
              Open AWS IAM Console - Create User
              <ExternalLink className="w-4 h-4" />
            </a>
          </div>
        </div>
      </div>

      {/* Credentials Form */}
      <form onSubmit={handleSave} className="bg-white border border-slate-200 rounded-sm">
        <div className="p-4 border-b border-slate-200 bg-slate-50">
          <h2 className="text-lg font-semibold text-slate-900">Enter Your AWS Credentials</h2>
          <p className="text-sm text-slate-600 mt-1">
            Paste the Access Key ID and Secret Access Key from AWS
          </p>
        </div>

        <div className="p-4 space-y-4">
          <div>
            <Label htmlFor="access_key_id" className="text-sm font-medium">
              Access Key ID <span className="text-red-500">*</span>
            </Label>
            <Input
              id="access_key_id"
              type="text"
              value={config.access_key_id}
              onChange={(e) => setConfig({ ...config, access_key_id: e.target.value })}
              placeholder="AKIAIOSFODNN7EXAMPLE"
              className="font-mono text-sm mt-1"
              required
            />
          </div>

          <div>
            <Label htmlFor="secret_access_key" className="text-sm font-medium">
              Secret Access Key <span className="text-red-500">*</span>
            </Label>
            <div className="relative mt-1">
              <Input
                id="secret_access_key"
                type={showSecret ? 'text' : 'password'}
                value={config.secret_access_key}
                onChange={(e) => setConfig({ ...config, secret_access_key: e.target.value })}
                placeholder="wJalrXUtnFEMI/K7MDENG/bPxRfiCYEXAMPLEKEY"
                className="font-mono text-sm pr-10"
                required
              />
              <button
                type="button"
                onClick={() => setShowSecret(!showSecret)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                {showSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            <p className="text-xs text-slate-500 mt-1">
              Your secret key is encrypted and stored securely
            </p>
          </div>

          <div>
            <Label htmlFor="region" className="text-sm font-medium">
              AWS Region
            </Label>
            <select
              id="region"
              value={config.region}
              onChange={(e) => setConfig({ ...config, region: e.target.value })}
              className="w-full rounded-sm border border-input bg-background px-3 py-2 text-sm mt-1"
            >
              {awsRegions.map((region) => (
                <option key={region.value} value={region.value}>
                  {region.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="p-4 border-t border-slate-200 bg-slate-50 flex gap-3">
          <Button
            type="submit"
            disabled={saving || testing}
            className="bg-slate-900 text-white hover:bg-slate-800 rounded-sm"
          >
            {saving ? (
              <>
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : testing ? (
              <>
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                Testing...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Save & Connect
              </>
            )}
          </Button>
          
          {hasConfig && !isConnected && (
            <Button
              type="button"
              variant="outline"
              onClick={handleTestConnection}
              disabled={testing}
              className="rounded-sm"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${testing ? 'animate-spin' : ''}`} />
              Test Connection
            </Button>
          )}
        </div>
      </form>

      {/* Security Info */}
      <div className="bg-slate-50 border border-slate-200 rounded-sm p-4">
        <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
          <Shield className="w-4 h-4" />
          Security Best Practices
        </h3>
        <ul className="space-y-2 text-sm text-slate-700">
          <li className="flex items-start gap-2">
            <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
            <span>Use a dedicated IAM user with minimal permissions (read-only access)</span>
          </li>
          <li className="flex items-start gap-2">
            <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
            <span>Never use your root account credentials</span>
          </li>
          <li className="flex items-start gap-2">
            <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
            <span>Rotate access keys periodically for enhanced security</span>
          </li>
          <li className="flex items-start gap-2">
            <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
            <span>You can revoke access anytime by deleting the IAM user in AWS</span>
          </li>
        </ul>
      </div>
    </div>
  );
}
